import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { hasPendingMedia, pollWhileMediaPending } from '@/common/media';

import {
  changeDocumentColor,
  addDocumentView,
  changeDocumentPermissions,
  createDocumentsFolder,
  deleteDocument,
  getAllDocuments,
  getDocumentById,
  getDocumentsByCursor,
  getDocumentViews,
  getRecentDocuments,
  renameDocument,
  shareDocument,
  unshareDocument,
  uploadDocumentsFile,
  uploadFinancialDocumentsFile,
} from './documents.api';
import type {
  CreateDocumentsFolderFormValues,
  DocumentPermission,
  DocumentsCursorFilters,
  DocumentsList,
  RecentDocumentsType,
  RenameDucumentFormValues,
  SingleDocument,
} from './documents.dto';

// A listing or a file showing a video that is still being processed keeps asking
// until it is not: the document service tells nobody when the encoder finishes.
const pollDocument = pollWhileMediaPending((document: SingleDocument) =>
  hasPendingMedia([document]),
);
const pollDocuments = pollWhileMediaPending(hasPendingMedia);

/**
 * The same listing as {@link useDocumentsQuery}, streamed rather than paged.
 *
 * Worth the second hook wherever the reader scrolls rather than jumps: the
 * offset path pays for a `COUNT(*) OVER ()` on every step to say how many pages
 * there are, and an endless row never puts that number on screen. A cursor also
 * cannot skip or repeat a row when something is added while the reader is part
 * way down, which offset paging does by construction.
 *
 * The cost is the ordering: the service resumes only its own — `type DESC, id
 * DESC` — so a listing that has to be A–Z stays on {@link useDocumentsQuery}.
 * Ids are UUIDv7, so for a single-kind listing that order is newest first.
 */
export const useDocumentsCursorQuery = (
  limit: number,
  parentId: null | string,
  filters: DocumentsCursorFilters,
) => {
  const { search, type } = filters;
  return useInfiniteQuery({
    // `'cursor'` keeps the two modes apart under the same prefix: the mutations
    // invalidate `['documents', parentId]`, and both listings have to answer to
    // that, but neither may be handed the other's pages.
    queryKey: ['documents', parentId, 'cursor', limit, search, type],
    queryFn: ({ pageParam }) => getDocumentsByCursor(limit, pageParam, parentId, filters),
    // Empty is "start at the beginning" — the first step sends no cursor at all.
    initialPageParam: '',
    getNextPageParam: (last) => (last.hasMore ? (last.nextCursor ?? undefined) : undefined),
    refetchInterval: pollWhileMediaPending((data: { pages: { items: SingleDocument[] }[] }) =>
      data.pages.some((page) => hasPendingMedia(page.items)),
    ),
  });
};

/**
 * What this person opened most recently, newest first.
 *
 * Nothing on the server fills this by itself: `document_views` has exactly one
 * writer, `POST /documents/view/:id`, so a screen that shows a document and does
 * not call {@link useAddDocumentViewMutation} leaves no trace here. The write is
 * an upsert on (document, user), so the same file opened twice stays one row
 * with a newer time rather than becoming two entries in this list.
 */
export const useRecentDocumentsQuery = (type: RecentDocumentsType, limit: number) =>
  useQuery({
    queryKey: ['documents-recent', type, limit],
    queryFn: () => getRecentDocuments(type, limit),
    refetchInterval: pollDocuments,
  });

export const useAllDocumentsQuery = () =>
  useQuery({
    queryKey: ['all-documents'],
    queryFn: () => getAllDocuments(),
    refetchInterval: pollWhileMediaPending((list: DocumentsList) => hasPendingMedia(list.items)),
  });

export const useDocumentByIdQuery = (id: string | null | undefined) =>
  useQuery({
    queryKey: ['document', id],
    queryFn: () => getDocumentById(id!),
    enabled: !!id,
    refetchInterval: pollDocument,
  });

// Fetch several documents by id, sharing the ['document', id] cache with
// useDocumentByIdQuery. `enabled: false` entries skip the request but keep the
// result array index-aligned with the input.
export const useDocumentsByIdsQueries = (items: { id: string; enabled: boolean }[]) =>
  useQueries({
    queries: items.map(({ id, enabled }) => ({
      queryKey: ['document', id],
      queryFn: () => getDocumentById(id),
      enabled,
      refetchInterval: pollDocument,
    })),
  });

export const useCreateDocumentsFolderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CreateDocumentsFolderFormValues) => createDocumentsFolder(values),
    onSuccess(_, { parentId }) {
      queryClient.invalidateQueries({ queryKey: ['documents', parentId] });
      queryClient.invalidateQueries({ queryKey: ['all-documents'] });
    },
  });
};

export const useRenameDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: RenameDucumentFormValues) => renameDocument(values),
    onSuccess(_, { parentId }) {
      queryClient.invalidateQueries({ queryKey: ['documents', parentId] });
      queryClient.invalidateQueries({ queryKey: ['all-documents'] });
      // The recent list shows names, so a rename it does not hear about leaves
      // the old one on screen.
      queryClient.invalidateQueries({ queryKey: ['documents-recent'] });
    },
  });
};

export const useChangeDocumentColorMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, color }: { id: string; color: string; parentId: null | string }) =>
      changeDocumentColor({ id, color }),
    onSuccess(_, { parentId }) {
      queryClient.invalidateQueries({ queryKey: ['documents', parentId] });
      queryClient.invalidateQueries({ queryKey: ['all-documents'] });
    },
  });
};

export const useUploadDocumentsFileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      parentId,
      name,
      color,
    }: {
      parentId?: null | string;
      file: File;
      name?: string;
      color?: null | string;
    }) => uploadDocumentsFile({ parentId, file, name, color }),
    onSuccess(_, { parentId }) {
      queryClient.invalidateQueries({ queryKey: ['documents', parentId] });
      queryClient.invalidateQueries({ queryKey: ['all-documents'] });
    },
  });
};

export const useUploadFinancialDocumentsFileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file }: { file: File }) => uploadFinancialDocumentsFile({ file }),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['all-documents'] });
    },
  });
};

export const useDeleteDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; parentId: null | string }) => deleteDocument(id),
    onSuccess(_, { parentId }) {
      queryClient.invalidateQueries({ queryKey: ['documents', parentId] });
      queryClient.invalidateQueries({ queryKey: ['all-documents'] });
      // A deleted document is still in the view log until this refetches, so
      // without it the recent list keeps offering a row that opens nothing.
      queryClient.invalidateQueries({ queryKey: ['documents-recent'] });
    },
  });
};

export const useDocumentViewsQuery = (id: string | null | undefined) =>
  useQuery({
    queryKey: ['document-views', id],
    queryFn: () => getDocumentViews(id!),
    enabled: !!id,
  });

export const useAddDocumentViewMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => addDocumentView(id),
    onSuccess(_, id) {
      queryClient.invalidateQueries({ queryKey: ['document-views', id] });
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      // The write this mutation makes is the only thing the recent list reads,
      // so without this the rail that shows it is stale the moment it is right.
      queryClient.invalidateQueries({ queryKey: ['documents-recent'] });
    },
  });
};

export const useShareDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shareWith }: { id: string; shareWith: string[]; parentId: null | string }) =>
      shareDocument({ id, shareWith }),
    onSuccess(_, { id, parentId }) {
      queryClient.invalidateQueries({ queryKey: ['documents', parentId] });
      queryClient.invalidateQueries({ queryKey: ['all-documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', id] });
    },
  });
};

export const useUnshareDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      unshareWith,
    }: {
      id: string;
      unshareWith: string[];
      parentId: null | string;
    }) => unshareDocument({ id, unshareWith }),
    onSuccess(_, { id, parentId }) {
      queryClient.invalidateQueries({ queryKey: ['documents', parentId] });
      queryClient.invalidateQueries({ queryKey: ['all-documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', id] });
    },
  });
};

export const useChangeDocumentPermissionsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      permissions,
    }: {
      id: string;
      permissions: DocumentPermission[];
      parentId: null | string;
    }) => changeDocumentPermissions({ id, permissions }),
    onSuccess(_, { id, parentId }) {
      queryClient.invalidateQueries({ queryKey: ['documents', parentId] });
      queryClient.invalidateQueries({ queryKey: ['all-documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', id] });
    },
  });
};
